<div class="bp-page">

  <div class="bp-banner bp-banner--info" *ngIf="isPortalRemoteReadOnly" role="status">
    <i class="pi pi-info-circle" aria-hidden="true"></i>
    <span>{{ 'FLEXI_BOOKING_PORTAL.READONLY' | translate }}</span>
  </div>

  <div class="bp-banner bp-banner--error" *ngIf="errorMessage" role="alert">
    <i class="pi pi-exclamation-circle" aria-hidden="true"></i>
    <span>{{ errorMessage }}</span>
  </div>

  <!-- ===================================================================
       Ordering. Catalogue on the left, the order itself on the right.
       =================================================================== -->
  <div class="bp-layout" *ngIf="!sent">

    <section class="bp-catalogue">
      <header class="bp-catalogue__head">
        <h2>{{ 'FLEXI_BOOKING_PORTAL.TITLE' | translate }}</h2>
        <span class="bp-step">{{ 'FLEXI_BOOKING_PORTAL.STEP_1' | translate }}</span>
      </header>
      <p class="bp-catalogue__intro">{{ textKey('INTRO') | translate }}</p>

      <div class="bp-search">
        <i class="pi pi-search" aria-hidden="true"></i>
        <input type="text"
               [(ngModel)]="query"
               (ngModelChange)="onQueryChange()"
               [disabled]="isPortalRemoteReadOnly"
               [attr.aria-label]="'FLEXI_BOOKING_PORTAL.SEARCH_LABEL' | translate"
               [placeholder]="'FLEXI_BOOKING_PORTAL.SEARCH_PLACEHOLDER' | translate" />
        <button type="button"
                class="bp-search__clear"
                *ngIf="isSearching"
                (click)="clearQuery()"
                [attr.aria-label]="'FLEXI_BOOKING_PORTAL.SEARCH_CLEAR' | translate">
          <i class="pi pi-times" aria-hidden="true"></i>
        </button>
      </div>

      <!-- Categories are filters, not a choice: picking one changes what the
           list shows, picking a service is what commits. Hidden while
           searching, which already spans every category. -->
      <div class="bp-chips" *ngIf="!isSearching">
        <button type="button"
                class="bp-chip"
                *ngFor="let c of categories"
                [class.is-active]="activeCategoryKey === c.key"
                [attr.aria-pressed]="activeCategoryKey === c.key"
                (click)="selectCategory(c.key)">
          <span class="bp-chip__label">{{ c.labelKey | translate }}</span>
          <span class="bp-chip__count" *ngIf="serviceCount(c) > 0">{{ serviceCount(c) }}</span>
        </button>
      </div>

      <div class="bp-group" *ngFor="let g of groups">
        <div class="bp-group__head" *ngIf="isSearching">
          <span class="bp-group__title">{{ g.categoryLabelKey | translate }}</span>
          <span class="bp-group__count">{{ g.entries.length }}</span>
        </div>

        <div class="bp-grid">
          <button type="button"
                  class="bp-service"
                  *ngFor="let e of g.entries"
                  [class.is-selected]="isSelected(e.categoryKey, e.serviceKey)"
                  [attr.aria-pressed]="isSelected(e.categoryKey, e.serviceKey)"
                  [disabled]="isPortalRemoteReadOnly"
                  [attr.title]="e.labelKey | translate"
                  (click)="selectService(e.categoryKey, e.serviceKey)">
            <span class="bp-service__dot" aria-hidden="true">
              <i class="pi pi-check"></i>
            </span>
            <span class="bp-service__body">
              <span class="bp-service__label">{{ e.labelKey | translate }}</span>
              <!-- The category is only worth naming when the list spans more
                   than one, which is exactly when searching. -->
              <span class="bp-service__cat" *ngIf="isSearching">{{ e.categoryLabelKey | translate }}</span>
            </span>
          </button>
        </div>
      </div>

      <!-- The catch-all category has no service list. Ordering under it is a
           real path, so it gets a card of its own rather than an empty grid. -->
      <div class="bp-other" *ngIf="isBrowsingOther">
        <h3>{{ 'FLEXI_BOOKING_PORTAL.OTHER_TITLE' | translate }}</h3>
        <p>{{ 'FLEXI_BOOKING_PORTAL.OTHER_NOTE' | translate }}</p>
        <button type="button"
                class="bp-btn bp-btn--ghost"
                [class.is-selected]="categoryKey === otherCategoryKey"
                [disabled]="isPortalRemoteReadOnly"
                (click)="selectOther()">
          <i class="pi pi-check" aria-hidden="true" *ngIf="categoryKey === otherCategoryKey"></i>
          {{ 'FLEXI_BOOKING_PORTAL.OTHER_ACTION' | translate }}
        </button>
      </div>

      <div class="bp-empty" *ngIf="hasNoResults">
        <i class="pi pi-search-minus" aria-hidden="true"></i>
        <p class="bp-empty__title">{{ 'FLEXI_BOOKING_PORTAL.NO_RESULTS' | translate:{ query: query } }}</p>
        <p class="bp-empty__body">{{ 'FLEXI_BOOKING_PORTAL.NO_RESULTS_HINT' | translate }}</p>
        <button type="button" class="bp-btn bp-btn--ghost" (click)="clearQuery()">
          {{ 'FLEXI_BOOKING_PORTAL.SEARCH_CLEAR' | translate }}
        </button>
      </div>
    </section>

    <aside class="bp-side">
      <form class="bp-card bp-order" (ngSubmit)="send()" novalidate>
        <div class="bp-order__head">
          <i class="pi pi-shopping-cart" aria-hidden="true"></i>
          <span class="bp-order__title">{{ 'FLEXI_BOOKING_PORTAL.ORDER_TITLE' | translate }}</span>
          <span class="bp-order__state" [class.is-ready]="canSend">
            {{ (canSend ? 'FLEXI_BOOKING_PORTAL.ORDER_READY' : 'FLEXI_BOOKING_PORTAL.ORDER_INCOMPLETE') | translate }}
          </span>
        </div>

        <div class="bp-order__pick" aria-live="polite">
          <ng-container *ngIf="hasPick; else noPick">
            <span class="bp-picked">
              <span class="bp-picked__cat">{{ pickedCategoryLabelKey | translate }}</span>
              <span class="bp-picked__name">
                {{ (pickedServiceLabelKey ? pickedServiceLabelKey : 'FLEXI_BOOKING_PORTAL.OTHER_PICKED') | translate }}
              </span>
            </span>
            <button type="button" class="bp-picked__change"
                    [disabled]="isPortalRemoteReadOnly"
                    (click)="clearPick()">
              {{ 'FLEXI_BOOKING_PORTAL.CHANGE' | translate }}
            </button>
          </ng-container>
          <ng-template #noPick>
            <span class="bp-nopick">
              <i class="pi pi-arrow-left" aria-hidden="true"></i>
              {{ 'FLEXI_BOOKING_PORTAL.NO_PICK' | translate }}
            </span>
          </ng-template>
        </div>

        <div class="bp-order__body">
          <p class="bp-step bp-step--block">{{ 'FLEXI_BOOKING_PORTAL.STEP_2' | translate }}</p>

          <div class="bp-field">
            <label for="bp-name">
              {{ 'FLEXI_BOOKING_PORTAL.CONTACT_NAME' | translate }}<span class="bp-req" aria-hidden="true">*</span>
              <span class="bp-field__note">{{ 'FLEXI_BOOKING_PORTAL.CONTACT_NAME_NOTE' | translate }}</span>
            </label>
            <input id="bp-name" type="text" name="contactName"
                   [(ngModel)]="contactName" (ngModelChange)="onContactInput()" (blur)="touch('name')"
                   maxlength="150" [disabled]="isPortalRemoteReadOnly"
                   [class.is-invalid]="visibleErrorKey('name')"
                   [attr.aria-invalid]="!!visibleErrorKey('name')"
                   [attr.aria-describedby]="visibleErrorKey('name') ? 'bp-name-err' : null" />
            <p class="bp-field__err" id="bp-name-err" *ngIf="visibleErrorKey('name') as err">
              <i class="pi pi-exclamation-circle" aria-hidden="true"></i>{{ err | translate }}
            </p>
          </div>

          <div class="bp-field">
            <label for="bp-email">
              {{ 'FLEXI_BOOKING_PORTAL.CONTACT_EMAIL' | translate }}<span class="bp-req" aria-hidden="true">*</span>
            </label>
            <input id="bp-email" type="email" name="contactEmail"
                   [(ngModel)]="contactEmail" (ngModelChange)="onContactInput()" (blur)="touch('email')"
                   maxlength="150" [disabled]="isPortalRemoteReadOnly"
                   [class.is-invalid]="visibleErrorKey('email')"
                   [attr.aria-invalid]="!!visibleErrorKey('email')"
                   [attr.aria-describedby]="visibleErrorKey('email') ? 'bp-email-err' : null" />
            <p class="bp-field__err" id="bp-email-err" *ngIf="visibleErrorKey('email') as err">
              <i class="pi pi-exclamation-circle" aria-hidden="true"></i>{{ err | translate }}
            </p>
          </div>

          <div class="bp-field">
            <label for="bp-phone">
              {{ 'FLEXI_BOOKING_PORTAL.CONTACT_PHONE' | translate }}<span class="bp-req" aria-hidden="true">*</span>
            </label>
            <input id="bp-phone" type="tel" name="contactPhone"
                   [(ngModel)]="contactPhone" (ngModelChange)="onContactInput()" (blur)="touch('phone')"
                   maxlength="50" [disabled]="isPortalRemoteReadOnly"
                   [class.is-invalid]="visibleErrorKey('phone')"
                   [attr.aria-invalid]="!!visibleErrorKey('phone')"
                   [attr.aria-describedby]="visibleErrorKey('phone') ? 'bp-phone-err' : null" />
            <p class="bp-field__err" id="bp-phone-err" *ngIf="visibleErrorKey('phone') as err">
              <i class="pi pi-exclamation-circle" aria-hidden="true"></i>{{ err | translate }}
            </p>
          </div>

          <!-- Where a user would look for a message box, and why there is none. -->
          <div class="bp-lock">
            <i class="pi pi-lock" aria-hidden="true"></i>
            <span>{{ 'FLEXI_BOOKING_PORTAL.NO_FREETEXT_NOTE' | translate }}</span>
          </div>

          <button type="submit" class="bp-btn bp-btn--send" [class.is-ready]="canSend"
                  [disabled]="isPortalRemoteReadOnly || submitting">
            <i class="pi" [ngClass]="submitting ? 'pi-spin pi-spinner' : 'pi-send'" aria-hidden="true"></i>
            {{ 'FLEXI_BOOKING_PORTAL.SEND' | translate }}
          </button>
          <p class="bp-blocker" *ngIf="blockerKey as blocker">{{ blocker | translate }}</p>
        </div>
      </form>

      <div class="bp-card bp-next">
        <p class="bp-step bp-step--block">{{ 'FLEXI_BOOKING_PORTAL.NEXT_TITLE' | translate }}</p>
        <div class="bp-next__step"><span>1</span>{{ textKey('NEXT_1') | translate }}</div>
        <div class="bp-next__step"><span>2</span>{{ 'FLEXI_BOOKING_PORTAL.NEXT_2' | translate }}</div>
        <div class="bp-next__step"><span>3</span>{{ 'FLEXI_BOOKING_PORTAL.NEXT_3' | translate }}</div>
      </div>
    </aside>
  </div>

  <!-- ===================================================================
       Receipt. The reference number is the only thing Kuba keeps and the
       only handle the customer has if they need to chase the order, so it
       gets a page rather than a banner.
       =================================================================== -->
  <div class="bp-done" *ngIf="sent as order" role="status">
    <div class="bp-card">
      <div class="bp-done__head">
        <span class="bp-done__icon" aria-hidden="true"><i class="pi pi-check"></i></span>
        <div class="bp-done__intro">
          <h2>{{ 'FLEXI_BOOKING_PORTAL.DONE_TITLE' | translate }}</h2>
          <p>
            {{ textKey(order.confirmationSent ? 'DONE_BODY_CONFIRMED' : 'DONE_BODY') | translate }}
          </p>
        </div>
        <span class="bp-ref">{{ order.reference }}</span>
      </div>

      <div class="bp-receipt">
        <div class="bp-receipt__row">
          <span>{{ 'FLEXI_BOOKING_PORTAL.RECEIPT_SERVICE' | translate }}</span>
          <span>{{ (order.serviceLabelKey ? order.serviceLabelKey : 'FLEXI_BOOKING_PORTAL.OTHER_PICKED') | translate }}</span>
        </div>
        <div class="bp-receipt__row">
          <span>{{ 'FLEXI_BOOKING_PORTAL.RECEIPT_CATEGORY' | translate }}</span>
          <span>{{ order.categoryLabelKey | translate }}</span>
        </div>
        <div class="bp-receipt__row">
          <span>{{ 'FLEXI_BOOKING_PORTAL.RECEIPT_CONTACT' | translate }}</span>
          <span>{{ order.contactName }} · {{ order.contactPhone }}</span>
        </div>

        <div class="bp-lock">
          <i class="pi pi-phone" aria-hidden="true"></i>
          <span>{{ textKey('DONE_HEALTH_NOTE') | translate }}</span>
        </div>
      </div>

      <div class="bp-done__foot">
        <button type="button" class="bp-btn bp-btn--send is-ready" (click)="orderAnother()">
          <i class="pi pi-plus" aria-hidden="true"></i>
          {{ 'FLEXI_BOOKING_PORTAL.ORDER_ANOTHER' | translate }}
        </button>
      </div>
    </div>
  </div>
</div>
